import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { AdSlot } from "@/components/AdSlot";
import { track } from "@/components/layernav/navConfig";
import { DeFiHero } from "@/components/sector/DeFiHero";
import { ProtocolSnapshot } from "@/components/sector/ProtocolSnapshot";
import { SectorLayerIndex } from "@/components/sector/SectorMarketPanel";
import { DeFiFrontLayer } from "@/components/sector/DeFiFrontLayer";
import { ProtocolActivity } from "@/components/sector/ProtocolActivity";
import { LiquidityIntelligence } from "@/components/sector/LiquidityIntelligence";
import { SecurityExploits } from "@/components/sector/SecurityExploits";
import { CryptoIntelligence } from "@/components/sector/CryptoIntelligence";
import { LatestCrypto, OriginalsSection, ResearchSection, ExplainersRail, RegulationSection } from "@/components/sector/SectorSections";
import { DeFiNerveCenter } from "@/components/sector/DeFiNerveCenter";
import { WhatChangedProtocols, ProtocolMovers } from "@/components/sector/ProtocolMovers";
import { EntitySyncProvider } from "@/components/sector/entitySync";
import { DeFiPulseStrip } from "@/components/sector/DeFiPulseStrip";
import { SectorClose } from "@/components/sector/SectorClose";

const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "NewsLayer", item: "https://newslayer.com/" },
    { "@type": "ListItem", position: 2, name: "DeFi", item: "https://newslayer.com/defi" },
  ],
};

export default function DeFiSectorPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["sector-defi"],
    queryFn: async () => (await api.get("/sectors/defi/overview")).data,
    refetchInterval: 60000,
    staleTime: 45000,
  });
  const { data: lower } = useQuery({
    queryKey: ["sector-defi-lower"],
    queryFn: async () => (await api.get("/sectors/defi/lower")).data,
    refetchInterval: 60000,
    staleTime: 45000,
  });

  useEffect(() => {
    track("sector_page_view", { sector: "defi" });
  }, []);

  return (
    <div data-testid="defi-sector-page" className="pb-16">
      <Seo title="DeFi News, TVL & Protocol Intelligence — NewsLayer"
        description="Protocols, liquidity, lending, decentralized markets and the infrastructure powering open finance. Live TVL, DEX volume, stablecoin supply and NewsLayer's DeFi intelligence in one console."
        path="/defi" jsonLd={BREADCRUMB_LD} />
      <DeFiHero data={data} />
      {/* First-view order below lg: Pulse → Trending → Protocol Snapshot → Layer Index → Front Layer */}
      <div className="lg:hidden max-w-[1400px] mx-auto px-4 sm:px-6 pt-6">
        <div className="grid md:grid-cols-2 gap-4 md:items-start">
          <ProtocolSnapshot data={data?.protocols} testid="protocol-snapshot-mobile" />
          <SectorLayerIndex testid="defi-layer-index-mobile" sector="defi" />
        </div>
      </div>
      {isLoading ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8" aria-hidden>
          <div className="h-[340px] rounded-xl border border-white/[0.06] bg-white/[0.02] animate-pulse" />
        </div>
      ) : (
        <DeFiFrontLayer lead={data?.lead || null} secondary={data?.secondary || []} />
      )}

      <ProtocolActivity data={lower?.protocol_activity} />
      <LiquidityIntelligence data={lower?.liquidity} />
      <CryptoIntelligence intel={lower?.intelligence} title="DeFi Intelligence" testid="defi-intelligence" sector="defi" />
      <SecurityExploits items={lower?.security || []} />
      <LatestCrypto items={lower?.latest || []} title="Latest DeFi" testid="latest-defi" sector="defi" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
        <AdSlot placement="sector_mid" />
      </div>
      <OriginalsSection items={lower?.originals || []} title="DeFi Originals" testid="defi-originals" sector="defi" />
      <ResearchSection items={lower?.research || []} testid="defi-research" sector="defi" />
      {/* DeFi intelligence zone: Nerve Center + What Changed + Movers composed with
          the strict Explained/Regulation sections (gates unchanged) */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid="defi-intelligence-zone">
        <EntitySyncProvider>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-stretch">
            <DeFiNerveCenter protocols={lower?.protocol_activity?.protocols || []} pulse={data?.pulse}
              meta={lower?.protocol_activity} />
            <WhatChangedProtocols protocols={lower?.protocol_activity?.protocols || []} />
          </div>
        </EntitySyncProvider>
        <div className="grid lg:grid-cols-2 gap-4 pt-6 items-stretch">
          <div className="space-y-8 min-w-0">
            <ExplainersRail embedded items={lower?.explainers || []} title="DeFi, Explained" testid="defi-explainers" sector="defi" />
            <RegulationSection embedded items={lower?.regulation || []} title="Regulation Affecting DeFi" testid="defi-regulation" sector="defi" />
          </div>
          <ProtocolMovers protocols={lower?.protocol_activity?.protocols || []} />
        </div>
      </section>
      <DeFiPulseStrip pulse={data?.pulse} />
      <SectorClose sector="defi" label="DeFi" />
    </div>
  );
}
